/**
 * Sync state: the code, and what the engine is currently doing.
 *
 * The code is the only secret the app holds, and it is deliberately kept in
 * its own localStorage key rather than inside settings — turning sync off and
 * wiping the key must never take the display preferences with it, and a
 * settings export must never carry the key out of the browser.
 *
 * `rev` is NOT persisted. It is this device's belief about the server's
 * revision, and a belief that survives a reload is a belief that can be wrong
 * after another device writes. The engine starts at 0 ("unknown"), and either
 * the first pull or the server's 409 tells it the truth.
 */

import { create } from 'zustand';

const KEY = 'meow:v1:sync';

export type SyncStatus = 'off' | 'idle' | 'syncing' | 'error';

/** Which line the UI shows; the engine never formats user-facing text. */
export type SyncFault = 'network' | 'code' | 'schema' | 'server';

interface SyncState {
  code: string | null;
  status: SyncStatus;
  fault: SyncFault | null;
  lastSyncedAt: number | null;
  setCode: (code: string | null) => void;
  report: (status: SyncStatus, fault?: SyncFault | null) => void;
  markSynced: (at: number) => void;
}

function loadCode(): string | null {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { code?: unknown };
    return typeof parsed.code === 'string' ? parsed.code : null;
  } catch {
    return null;
  }
}

function persistCode(code: string | null): void {
  try {
    if (code === null) globalThis.localStorage?.removeItem(KEY);
    else globalThis.localStorage?.setItem(KEY, JSON.stringify({ code }));
  } catch {
    /* private mode — sync then lasts only for this tab, which is honest enough */
  }
}

export const useSync = create<SyncState>((set) => {
  const code = loadCode();
  return {
    code,
    status: code ? 'idle' : 'off',
    fault: null,
    lastSyncedAt: null,

    setCode: (next) => {
      persistCode(next);
      set({ code: next, status: next ? 'idle' : 'off', fault: null, lastSyncedAt: null });
    },
    report: (status, fault = null) => set({ status, fault }),
    // Guarded: `stop()` clears the code mid-operation, and the success path
    // that follows must not report a stopped device as idly syncing.
    markSynced: (at) =>
      set((s) => (s.code ? { status: 'idle', fault: null, lastSyncedAt: at } : s)),
  };
});
